import {
   DashTimeRange,
   DataTableChartFormat,
   ClientChartType,
   offsetDateByTime,
   linebarFormatExpand,
   donutFormatExpand,
   DonutChartFormat,
   statsFormatExpand,
} from '@metrika/types';
import { PayloadAction } from '@reduxjs/toolkit';
import Axios from 'axios';
import { call, cancelled, put, select } from 'redux-saga/effects';
import { handleAPIFailure } from '../../../_shared/api';
import { getChart } from '../../api';
import { RemoteChart } from '../../api/io';
import type {
   ChartState,
   ChartTypeResponder,
   ChartStatePartial,
   ChartStateDatatablePartial,
} from '../../charts/_types';
import { getResponderForType } from '../../charts/responders';
import type { DashboardFilter } from '../../hooks/dashFilters.types';
import { loadChartFailed, loadChartSuccess, subscribeToChart, updateChartEnd } from '../reducers';
import { makeGetChart, makeGetFormat } from '../selectors';

export function* updateChartSaga(action: PayloadAction<{ viewId: string; cursor?: 'back' | 'forward' }>) {
   const { viewId, cursor } = action.payload;
   console.log(`API >> Searching chart ${viewId}`);
   const cancelTokenSource = Axios.CancelToken.source();

   try {
      const currentChart: ChartState = yield select(makeGetChart(viewId));
      if (!currentChart) throw new Error(`Chart not found: ${viewId}`);
      if (
         currentChart.isLoading ||
         currentChart.type !== 'datatable' ||
         !currentChart.paged ||
         (currentChart.paged.page === 0 && cursor === 'back') ||
         (!currentChart.paged.nextPage && cursor === 'forward')
      ) {
         yield put(updateChartEnd({ viewId }));
         return;
      }

      /* it is a paged table let's go */
      const cancelToken = cancelTokenSource.token;
      const currentPage = currentChart.paged;

      const timeRange: { range: DashTimeRange; start?: string; end?: string } | undefined = yield select(
         (state) => state.charts.timeRange,
      );
      const timeEnd: string = yield select((state) => state.charts.timeEnd);
      const end =
         timeRange?.range === 'custom' && timeRange?.end ? new Date(timeRange.end) : new Date(timeEnd || Date.now());
      const start =
         timeRange?.range === 'custom' && timeRange?.start
            ? new Date(timeRange.start)
            : !!timeRange?.range && timeRange.range !== 'custom'
              ? offsetDateByTime(end, timeRange.range)
              : end;

      const filters: DashboardFilter[] = yield select((s) => s.charts.filters);
      const maxSamples = currentChart.metadata?.maxSamples || 1;
      const { dataId, search, sortCol, sortOrder } = currentChart;

      const currentFormat: DataTableChartFormat = yield select(makeGetFormat(viewId));
      const columnsToFilter = currentFormat.columns
         .filter((col) => col.searchName)
         .map((c) => c.searchName)
         .join(',');

      //set cursor only if it is page navigation
      let extraQuery = cursor
         ? `page=${currentPage.page + (cursor === 'forward' ? 1 : -1)}&cursor=${currentPage.cursor}`
         : `${
              sortCol && sortOrder && !!sortOrder[sortCol] && sortOrder[sortCol] !== 'NONE'
                 ? `sort=${sortCol}&sortOrder=${(sortOrder[sortCol] || '').toLowerCase()}`
                 : ''
           }`;
      if (!cursor && search && columnsToFilter) {
         extraQuery = `${extraQuery ? extraQuery + '&' : ''}filter=${columnsToFilter}&filterQuery=${search}`;
      }
      extraQuery = currentChart.metadata.extraQuery ? `${extraQuery}&${currentChart.metadata.extraQuery}` : extraQuery;
      const resp: RemoteChart = yield call(getChart, {
         id: dataId,
         start,
         end,
         filters,
         maxSamples,
         cancelToken,
         extraQuery,
      });

      const view = resp.metadata.views ? resp.metadata.views[viewId.split('$')[0]] : undefined;
      if (!view) throw new Error(`Chart view not found: ${viewId}`);

      if (view.type === 'linebar-time' && (resp.type === 'line' || resp.type === 'bar')) {
         // Expand the format to include the dynamic time series
         view.format = linebarFormatExpand(resp.data, view.format);
      }
      if (view.type === 'donut' && resp.type === 'stats') {
         // Expand the format to include the dynamic time series
         // @ts-ignore
         view.format = donutFormatExpand(resp.data, view.format as DonutChartFormat) as DonutChartFormat;
      }

      if (view.type === 'statistic' && resp.type === 'stats') {
         // Expand the format to include the dynamic stats ids
         // @ts-ignore
         view.format = statsFormatExpand(resp.data, view.format as StatisticChartFormat) as StatisticChartFormat;
      }

      const { format, type } = view;
      const responder: ChartTypeResponder<ClientChartType> = yield call(getResponderForType, view.type);
      const data: ReturnType<ChartTypeResponder<ClientChartType>['transform']> = yield call(
         responder.transform,
         resp.data,
         format,
      );
      const chart: ChartStatePartial = {
         id: viewId,
         dataId,
         updateMethod: resp.metadata.updateMethod,
         type,
         data,
         interval: resp.metadata.interval,
      };

      if (chart.type === 'datatable' && resp.type === 'table_paged') {
         (chart as ChartStateDatatablePartial).paged = {
            page: resp.metadata.page,
            cursor: resp.metadata.cursor,
            nextPage: resp.metadata.nextPage,
            totalPages: resp.metadata.totalPages,
         };
      }

      yield put(loadChartSuccess({ chart, format }));
      yield put(
         subscribeToChart({
            id: viewId,
            updateMethod: chart.updateMethod,
            maxSamples: maxSamples,
         }),
      );
   } catch (e: unknown) {
      yield call(handleAPIFailure, e, (errorMessage: string) => loadChartFailed({ id: viewId, errorMessage }));
   } finally {
      const hasBeenCancelled: boolean = yield cancelled();
      if (hasBeenCancelled) {
         cancelTokenSource.cancel('cancelled from user');
         console.log(`API >> CANCELLED Searching chart ${viewId}`);
      }
   }
}
